if (!customElements.get('custom-related-products')) {
  customElements.define('custom-related-products', class RelatedProductsV1 extends HTMLElement {
    apiSectionId = 'api-related-products-v1-sliders';
    initialized = false;

    constructor() {
      super();
      this.sectionSettings = JSON.parse(this.getAttribute("data-settings").replaceAll("/'", '"'));
    }

    init() {
      if (this.initialized) {
        return;
      }
      this.initialized = true;
      this.createSlider();
    }

    createSlider() {
      if (this.mySwiper) {
        return;
      }
      this.mySwiper = new Swiper(this.querySelector('.swiper-container'), {
        slidesPerView: 1.5,
        loop: false,
        // slidesPerGroup: 4,
        speed: 500,
        spaceBetween: 16,
        slidesOffsetBefore: 18,
        slidesOffsetAfter: 18,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: this.querySelector('.swiper-button-next'),
          prevEl: this.querySelector('.swiper-button-prev'),
        },
        scrollbar: {
          el: this.querySelector('.swiper-scrollbar'),
          draggable: true,
        },
        breakpoints: {
          480: {
            slidesOffsetBefore: 30,
            slidesOffsetAfter: 30,
          },
          590: {
            slidesPerView: 2.3,
            spaceBetween: 16,
            slidesOffsetBefore: 30,
            slidesOffsetAfter: 30,
          },
          768: {
            slidesPerView: 3,
            spaceBetween: 24,
            slidesOffsetBefore: 0,
            slidesOffsetAfter: 0,
            slidesPerGroup: 3,
          },
          1200: {
            slidesPerView: 4,
            spaceBetween: 24,
            slidesOffsetBefore: 0,
            slidesOffsetAfter: 0,
            slidesPerGroup: 4,
          },
          1400: {
            slidesPerView: 5,
            spaceBetween: 24,
            slidesOffsetBefore: 0,
            slidesOffsetAfter: 0,
            slidesPerGroup: 5,
          },
        },
      });
    }

    connectedCallback() {
      this.observeElement(this, this.handleInViewport.bind(this));
      const productId = this.getAttribute("data-product-id");
      if (this.sectionSettings.enable_auto_recommendations && productId) {
        if (this.sectionSettings.enable_smart_recommendations) {
          this.initSmartRecommendations();
        } else {
          this.initShopifyRecommendations();
        }
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.destroySlider();
    }

    getRecommendationsLimit() {
      const parsed = Number(this.sectionSettings.limit);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
      return 8;
    }

    getShopifyRecommendationsLimit() {
      return Math.min(this.getRecommendationsLimit(), 10);
    }

    hasValue(value) {
      return value != null && value !== '';
    }

    isUsableSlide(slider, productId, usedIds) {
      const id = slider.getAttribute('data-slide-product-id');
      const available = slider.getAttribute('data-slide-product-available') === 'true';
      if (!available || !id || String(id) === String(productId) || usedIds.has(String(id))) {
        return false;
      }
      return true;
    }

    collectSlides(sliders, productId, usedIds, renderSliders, limit) {
      for (const slider of sliders) {
        if (renderSliders.length >= limit) {
          break;
        }
        if (!this.isUsableSlide(slider, productId, usedIds)) {
          continue;
        }
        usedIds.add(String(slider.getAttribute('data-slide-product-id')));
        renderSliders.push(slider);
      }
    }

    initShopifyRecommendations() {
      const productId = this.getAttribute("data-product-id");
      const limit = this.getShopifyRecommendationsLimit();
      const productsWrapper = this.querySelector("[data-swiper-wrapper]");
      if (!productId || !productsWrapper) {
        return;
      }

      const route = window.theme.routes.product_recommendations_url || '/recommendations/products/';
      const requestUrl = `${route}?section_id=related-products&limit=${limit}&product_id=${productId}`;

      themeVendor.axios
        .get(requestUrl)
        .then((response) => {
          const fresh = document.createElement('div');
          fresh.innerHTML = response.data;

          const inner = fresh.querySelector('[data-swiper-wrapper]');
          if (!inner) {
            return;
          }

          productsWrapper.innerHTML = inner.innerHTML;
          this.refreshSliderIfNeeded();
        })
        .catch(function (error) {
          console.warn(error);
        });
    }

    // Smart recommendations.
    initSmartRecommendations() {
      const productId = this.getAttribute("data-product-id");
      const collectionHandle = this.getAttribute("data-collection-handle");
      const productType = this.getAttribute("data-product-type");
      this.productsWrapper = this.querySelector("[data-swiper-wrapper]");
      if (!this.productsWrapper) {
        return;
      }

      if (this.hasValue(collectionHandle) || this.hasValue(productType)) {
        this.loadSmartRecommendations(collectionHandle, productType, productId);
      } else {
        console.warn('No collection handle or product type found');
        this.initShopifyRecommendations();
      }
    }

    // Load smart recommendations from the request url.
    async loadSmartRecommendations(collectionHandle, productType, productId) {
      const limit = this.getRecommendationsLimit();
      const renderSliders = [];
      const usedIds = new Set();
      const collectionRequestUrl = `${window.theme.routes.root_url}collections/${collectionHandle}?section_id=${this.apiSectionId}&sort_by=best-selling`;
      const productTypeRequestUrl = `${window.theme.routes.root_url}collections/all-products?section_id=${this.apiSectionId}&sort_by=best-selling&filter.p.product_type=${encodeURIComponent(productType)}`;

      try {
        if (this.hasValue(collectionHandle)) {
          const { sliders } = await this.requestSmartRecommendations(collectionRequestUrl);
          this.collectSlides(sliders, productId, usedIds, renderSliders, limit);
        }

        if (this.hasValue(productType) && renderSliders.length < limit) {
          const { sliders } = await this.requestSmartRecommendations(productTypeRequestUrl);
          this.collectSlides(sliders, productId, usedIds, renderSliders, limit);
        }

        if (renderSliders.length < limit) {
          const { sliders } = await this.requestShopifyRecommendations(productId);
          this.collectSlides(sliders, productId, usedIds, renderSliders, limit);
        }
      } catch (error) {
        console.warn(error);
      }

      this.productsWrapper.innerHTML = renderSliders.map(slider => slider.outerHTML).join('');
      this.refreshSliderIfNeeded();
    }

    async requestSmartRecommendations(requestUrl) {
      try {
        const response = await fetch(requestUrl);
        if (!response.ok) {
          console.warn('Smart recommendations request failed', response.status, requestUrl);
          return { sliders: [] };
        }
        const data = await response.text();
        const fresh = document.createElement('div');
        fresh.innerHTML = data;
        return {
          sliders: [...fresh.querySelectorAll('[data-related-products-slide]')],
        };
      } catch (error) {
        console.warn('Smart recommendations request failed', error);
        return { sliders: [] };
      }
    }

    async requestShopifyRecommendations(productId) {
      try {
        const route = window.theme.routes.product_recommendations_url || '/recommendations/products/';
        const limit = this.getShopifyRecommendationsLimit();
        const requestUrl = `${route}?section_id=${this.apiSectionId}&limit=${limit}&product_id=${productId}`;
        const response = await fetch(requestUrl);
        if (!response.ok) {
          console.warn('Shopify recommendations request failed', response.status, requestUrl);
          return { sliders: [] };
        }
        const data = await response.text();
        const fresh = document.createElement('div');
        fresh.innerHTML = data;
        return {
          sliders: [...fresh.querySelectorAll('[data-related-products-slide]')],
        };
      } catch (error) {
        console.warn('Shopify recommendations request failed', error);
        return { sliders: [] };
      }
    }

    refreshSliderIfNeeded() {
      if (!this.initialized) {
        return;
      }
      this.destroySlider();
      this.createSlider();
    }

    destroySlider() {
      if (this.mySwiper) {
        this.mySwiper.destroy(true, true);
        this.mySwiper = null;
      }
    }

    handleInViewport() {
      // 初始化元素
      this.init();
    }

    observeElement(element, callback) {
      // 创建一个 IntersectionObserver 实例
      const observer = new IntersectionObserver((entries, observerInstance) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            callback();
            // 只执行一次，停止观察
            observerInstance.unobserve(entry.target);
          }
        });
      }, {
        // 视口外 1/2 高度以内触发
        root: null,
        threshold: 0.5
      });
      // 如果已经在视口内,立即执行回调
      if (this.isElementInViewport(element)) {
        callback();
        return;
      }
      observer.observe(element);
      this.observer = observer;
    }

    // 判断元素是否在视口内
    isElementInViewport(el) {
      const rect = el.getBoundingClientRect();
      return (
        rect.top >= -el.offsetHeight / 2 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) + el.offsetHeight / 2
      );
    }
  });
}
